import React from 'react';
import { parametrosModalidade } from '../modalidades';
import { posicaoZCamadaCem2x2 } from './centralizacaoCem2x2';

const C_PAL_FILL    = '#2a2a3a';
const C_PAL_STROKE  = '#555';
const C_BOX_FILL    = '#d4b3d8';
const C_BOX_STROKE  = '#6a4a72';
const C_COTA        = '#888';
const C_OVERHANG    = '#e5a14a';

const PAD          = 60;   // espaço de borda (mm coord)
const COTA_GAP     = 28;   // distância da borda ao traço de cota
const COTA_TXT     = 52;   // distância da borda ao texto de cota
const FONT         = 38;   // fontSize em coordenadas mm
const SW_PAL       = 10;   // strokeWidth do palete
const SW_BOX       = 5;    // strokeWidth das caixas

// ─────────────────────────────────────────────────────────────────────────────
// Vista Superior
// ─────────────────────────────────────────────────────────────────────────────

function VistaSuperior({ c, l, n, isLong, balanco, parametros }) {
  const PALLET_C = parametros.comprimentoMm;
  const PALLET_L = parametros.larguraMm;
  let boxes = [];

  if (isLong) {
    const boxX = (PALLET_C - c) / 2;  // X centrado (negativo se balanço)
    for (let i = 0; i < n; i++) {
      boxes.push({ x: boxX, y: i * l, w: c, h: l });  // y=0 = frente
    }
  } else {
    const totalW  = n * l;
    const offsetX = (PALLET_C - totalW) / 2;  // X centrado
    for (let i = 0; i < n; i++) {
      boxes.push({ x: offsetX + i * l, y: 0, w: l, h: c });  // y=0 = frente
    }
  }

  const minBoxX    = boxes.length ? Math.min(...boxes.map(b => b.x)) : 0;
  const maxBoxX    = boxes.length ? Math.max(...boxes.map(b => b.x + b.w)) : PALLET_C;
  const maxBoxEndY = boxes.length ? Math.max(...boxes.map(b => b.y + b.h)) : PALLET_L;

  const vbMinX = Math.min(minBoxX, 0) - PAD;
  const vbMaxX = Math.max(maxBoxX, PALLET_C) + PAD + COTA_TXT + FONT;
  const vbMinY = -PAD;
  const vbMaxY = Math.max(maxBoxEndY, PALLET_L) + PAD + COTA_TXT + FONT;

  const overhangPerSide = Math.max(0, balanco);

  return (
    <svg
      viewBox={`${vbMinX} ${vbMinY} ${vbMaxX - vbMinX} ${vbMaxY - vbMinY}`}
      className="pal-viz-svg"
      preserveAspectRatio="xMidYMid meet"
    >
      {/* Palete */}
      <rect
        x={0} y={0} width={PALLET_C} height={PALLET_L}
        fill={C_PAL_FILL} stroke={C_PAL_STROKE} strokeWidth={SW_PAL}
      />

      {/* Caixas da 1ª camada */}
      {boxes.map((b, i) => (
        <rect
          key={i}
          x={b.x} y={b.y} width={b.w} height={b.h}
          fill={C_BOX_FILL} stroke={C_BOX_STROKE} strokeWidth={SW_BOX}
          fillOpacity={0.85}
        />
      ))}

      {/* Indicadores de balanço */}
      {overhangPerSide > 0 && isLong && (
        <>
          <line x1={minBoxX} y1={-18} x2={0} y2={-18} stroke={C_OVERHANG} strokeWidth={3} strokeDasharray="10 6" />
          <text x={(minBoxX + 0) / 2} y={-26} textAnchor="middle" fill={C_OVERHANG} fontSize={FONT - 4}>
            {Math.round(overhangPerSide)}mm
          </text>
          <line x1={PALLET_C} y1={-18} x2={maxBoxX} y2={-18} stroke={C_OVERHANG} strokeWidth={3} strokeDasharray="10 6" />
          <text x={(PALLET_C + maxBoxX) / 2} y={-26} textAnchor="middle" fill={C_OVERHANG} fontSize={FONT - 4}>
            {Math.round(overhangPerSide)}mm
          </text>
        </>
      )}

      {/* Cota comprimento (horizontal) */}
      <line x1={0} y1={PALLET_L + COTA_GAP} x2={PALLET_C} y2={PALLET_L + COTA_GAP}
        stroke={C_COTA} strokeWidth={3} />
      <text x={PALLET_C / 2} y={PALLET_L + COTA_TXT}
        textAnchor="middle" fill={C_COTA} fontSize={FONT}>
        {PALLET_C}mm
      </text>

      {/* Cota largura (vertical, rotacionada) */}
      <line x1={PALLET_C + COTA_GAP} y1={0} x2={PALLET_C + COTA_GAP} y2={PALLET_L}
        stroke={C_COTA} strokeWidth={3} />
      <text
        x={PALLET_C + COTA_TXT} y={PALLET_L / 2}
        textAnchor="middle" fill={C_COTA} fontSize={FONT}
        transform={`rotate(-90, ${PALLET_C + COTA_TXT}, ${PALLET_L / 2})`}
      >
        {PALLET_L}mm
      </text>
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Vista Superior CEM 2+2
// ─────────────────────────────────────────────────────────────────────────────

function VistaSuperiorCem2x2({ c, l, indiceCamada, sobreposicao, parametros, centralizacaoConjuntoZ }) {
  // Mesmo referencial do 3D: X cresce no comprimento, Z positivo fica na frente.
  // No SVG, y cresce para tras; por isso o deslocamento em Z soma em y.
  const PALLET_C = parametros.comprimentoMm;
  const PALLET_L = parametros.larguraMm;
  const ocupacaoC = c + 2 * l;
  const xInicio = (PALLET_C - ocupacaoC) / 2;
  const espelhada = indiceCamada % 2 === 1;
  const base = [
    { x: xInicio, y: 0, w: c, h: l, orientacao: 'longitudinal' },
    { x: xInicio, y: l, w: c, h: l, orientacao: 'longitudinal' },
    { x: xInicio + c, y: 0, w: l, h: c, orientacao: 'girada' },
    { x: xInicio + c + l, y: 0, w: l, h: c, orientacao: 'girada' },
  ];
  const boxes = base.map(box => ({
    ...box,
    x: espelhada ? PALLET_C - box.x - box.w : box.x,
    y: box.y - posicaoZCamadaCem2x2(centralizacaoConjuntoZ, sobreposicao, indiceCamada),
  }));
  const minX = Math.min(0, ...boxes.map(box => box.x));
  const maxX = Math.max(PALLET_C, ...boxes.map(box => box.x + box.w));
  const maxY = Math.max(PALLET_L, ...boxes.map(box => box.y + box.h));
  const balancoComposicao = Math.max(0, ocupacaoC - PALLET_C);

  return (
    <>
      <svg data-layout="cem-2x2" data-camada={indiceCamada + 1}
        viewBox={`${minX - PAD} ${-PAD} ${maxX - minX + 2 * PAD + COTA_TXT + FONT} ${maxY + 2 * PAD + COTA_TXT + FONT}`}
        className="pal-viz-svg" preserveAspectRatio="xMidYMid meet">
        <rect x={0} y={0} width={PALLET_C} height={PALLET_L}
          fill={C_PAL_FILL} stroke={C_PAL_STROKE} strokeWidth={SW_PAL} />
        {boxes.map((box, i) => (
          <rect key={i} data-caixa={box.orientacao}
            x={box.x} y={box.y} width={box.w} height={box.h}
            fill={C_BOX_FILL} stroke={C_BOX_STROKE} strokeWidth={SW_BOX}
            fillOpacity={0.85} />
        ))}
        {balancoComposicao > 0 && (
          <>
            <line data-balanco="inicio" x1={minX} y1={-18} x2={0} y2={-18}
              stroke={C_OVERHANG} strokeWidth={3} strokeDasharray="10 6" />
            <line data-balanco="fim" x1={PALLET_C} y1={-18} x2={maxX} y2={-18}
              stroke={C_OVERHANG} strokeWidth={3} strokeDasharray="10 6" />
          </>
        )}
        <line x1={0} y1={PALLET_L + COTA_GAP} x2={PALLET_C} y2={PALLET_L + COTA_GAP}
          stroke={C_COTA} strokeWidth={3} />
        <text x={PALLET_C / 2} y={PALLET_L + COTA_TXT}
          textAnchor="middle" fill={C_COTA} fontSize={FONT}>{PALLET_C}mm</text>
        <line x1={PALLET_C + COTA_GAP} y1={0} x2={PALLET_C + COTA_GAP} y2={PALLET_L}
          stroke={C_COTA} strokeWidth={3} />
        <text x={PALLET_C + COTA_TXT} y={PALLET_L / 2}
          textAnchor="middle" fill={C_COTA} fontSize={FONT}
          transform={`rotate(-90, ${PALLET_C + COTA_TXT}, ${PALLET_L / 2})`}>{PALLET_L}mm</text>
      </svg>
      {balancoComposicao > 0 && (
        <p className="pal-viz-label">
          Balanço da composição: {balancoComposicao} mm total
          ({(balancoComposicao / 2).toLocaleString('pt-BR')} mm por ponta)
        </p>
      )}
    </>
  );
}

// Vista Lateral

function VistaLateral({ c, l, n, a, numCamadas, isLong, sobreposicao, parametros, larguraOcupada, centralizacaoConjuntoZ = 0 }) {
  const PALLET_L = parametros.larguraMm;
  const PALLET_STAND = parametros.alturaPaleteMm;
  // Vista lateral (eixo Z): longitudinal=n caixas ao longo de Z, atravessada=1 box de profundidade c
  const boxW      = larguraOcupada ?? (isLong ? n * l : c);
  const stackH    = numCamadas * a;
  const boxXFront = PALLET_L - boxW;  // direita = frente (+Z); negativo se ultrapassar o palete

  const latBoxes = Array.from({ length: numCamadas }, (_, i) => ({
    x: boxXFront + posicaoZCamadaCem2x2(centralizacaoConjuntoZ, sobreposicao, i),
    y: i * a,
    w: boxW,
    h: a,
  }));

  const minX = Math.min(boxXFront + centralizacaoConjuntoZ - sobreposicao, 0);
  const maxX = PALLET_L;

  const vbMinX = minX - PAD;
  const vbMaxX = maxX + PAD + COTA_TXT + FONT;
  const vbMinY = -PAD;
  const vbMaxY = stackH + PALLET_STAND + PAD + COTA_TXT;

  const cotaX  = PALLET_L + COTA_GAP;
  const cotaTX = PALLET_L + COTA_TXT;

  return (
    <svg
      viewBox={`${vbMinX} ${vbMinY} ${vbMaxX - vbMinX} ${vbMaxY - vbMinY}`}
      className="pal-viz-svg"
      preserveAspectRatio="xMidYMid meet"
    >
      {/* Caixas empilhadas */}
      {latBoxes.map((b, i) => (
        <rect
          key={i}
          x={b.x} y={b.y} width={b.w} height={b.h}
          fill={C_BOX_FILL} stroke={C_BOX_STROKE} strokeWidth={SW_BOX}
          fillOpacity={i % 2 === 0 ? 0.85 : 0.68}
        />
      ))}

      {/* Palete (estrutura) */}
      <rect
        x={0} y={stackH} width={PALLET_L} height={PALLET_STAND}
        fill={C_PAL_FILL} stroke={C_PAL_STROKE} strokeWidth={SW_PAL}
      />

      {/* Cota de altura da pilha */}
      <line x1={cotaX} y1={0} x2={cotaX} y2={stackH}
        stroke={C_COTA} strokeWidth={3} />
      {/* Extremidades da cota */}
      <line x1={cotaX - 14} y1={0}        x2={cotaX + 14} y2={0}        stroke={C_COTA} strokeWidth={3} />
      <line x1={cotaX - 14} y1={stackH}   x2={cotaX + 14} y2={stackH}   stroke={C_COTA} strokeWidth={3} />
      <text
        x={cotaTX} y={stackH / 2}
        textAnchor="middle" fill={C_COTA} fontSize={FONT}
        transform={`rotate(-90, ${cotaTX}, ${stackH / 2})`}
      >
        {Math.round(stackH)}mm
      </text>
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Componente exportado
// ─────────────────────────────────────────────────────────────────────────────

function Visualizacao2D({ plano, volume, campos, parametros = parametrosModalidade('embalagem'), modalidade = 'embalagem', centralizacaoConjuntoZ = 0 }) {
  // Usa `campos` (do editor, sem debounce) para feedback em tempo real.
  // Usa `plano` para dimensões salvas (alertas, stats). Se plano não existir
  // ainda, deriva tudo dos campos + volume.
  if (!volume) return null;

  const n             = campos?.caixasPorCamada  ?? plano?.caixas_por_camada    ?? 1;
  const numCam        = campos?.numCamadas       ?? plano?.num_camadas          ?? 1;
  const orientacao    = campos?.orientacao       ?? plano?.layout_orientacao    ?? 'longitudinal';
  const balanco       = plano?.balanco_lateral_mm ?? 0;
  const sobreposicao  = campos?.sobreposicaoMm   ?? plano?.sobreposicao_mm     ?? 50;

  const c = volume.comprimento_mm;
  const l = volume.largura_mm;
  const a = volume.altura_mm;
  const isLong = orientacao === 'longitudinal';
  const nAlinhadas = campos?.caixasAlinhadas ?? plano?.caixas_alinhadas ?? n;
  const nGiradas = campos?.caixasGiradas ?? plano?.caixas_giradas ?? 0;
  const cem2x2 = modalidade === 'lojas_cem' && isLong
    && nAlinhadas === 2 && nGiradas === 2;

  if (cem2x2) {
    return (
      <div className="pal-viz-container">
        <p className="pal-viz-label">Vista Superior — 1ª camada</p>
        <VistaSuperiorCem2x2 c={c} l={l} indiceCamada={0}
          sobreposicao={sobreposicao} parametros={parametros}
          centralizacaoConjuntoZ={centralizacaoConjuntoZ} />
        {numCam > 1 && (
          <>
            <p className="pal-viz-label" style={{ marginTop: '1.25rem' }}>
              Vista Superior — 2ª camada (espelhada, deslocada {sobreposicao} mm)
            </p>
            <VistaSuperiorCem2x2 c={c} l={l} indiceCamada={1}
              sobreposicao={sobreposicao} parametros={parametros}
              centralizacaoConjuntoZ={centralizacaoConjuntoZ} />
          </>
        )}
        <p className="pal-viz-label" style={{ marginTop: '1.25rem' }}>
          Vista Lateral — {numCam} camada{numCam !== 1 ? 's' : ''}
        </p>
        <VistaLateral c={c} l={l} n={n} a={a} numCamadas={numCam}
          isLong={isLong} sobreposicao={sobreposicao} parametros={parametros}
          larguraOcupada={Math.max(2 * l, c)}
          centralizacaoConjuntoZ={centralizacaoConjuntoZ} />
      </div>
    );
  }

  return (
    <div className="pal-viz-container">
      <p className="pal-viz-label">Vista Superior — 1ª camada</p>
      <VistaSuperior c={c} l={l} n={n} isLong={isLong} balanco={balanco} parametros={parametros} />

      <p className="pal-viz-label" style={{ marginTop: '1.25rem' }}>
        Vista Lateral — {numCam} camada{numCam !== 1 ? 's' : ''}
      </p>
      <VistaLateral c={c} l={l} n={n} a={a} numCamadas={numCam} isLong={isLong} sobreposicao={sobreposicao} parametros={parametros} />
    </div>
  );
}

export default Visualizacao2D;
